/**
 * CIVIK — Parcours numérique de candidature professionnelle (/candidature) — 2026-09-15
 * -------------------------------------------------------------------------------------
 * Ordre du parcours (correction du 15/09, §8) :
 *   1. création du compte (/inscription) → 2. sélection du profil → 3. questionnaire →
 *   4. formation CIVIK en ligne → 5. diplômes et justificatifs → 6. envoi du dossier →
 *   7. instruction par CIVIK → 8. notification de la décision → 9. activation du statut.
 * Le candidat voit son état d'avancement (§10) et l'action suivante requise.
 * Microcopy (§11) : CANDIDATER EN LIGNE · COMMENCER MA CANDIDATURE · POURSUIVRE MA
 * CANDIDATURE · VALIDER ET CONTINUER · COMMENCER / POURSUIVRE LA FORMATION ·
 * AJOUTER UN JUSTIFICATIF · TRANSMETTRE MA CANDIDATURE.
 *
 * API : /api/candidatures (définition, me, création, questionnaire, formation,
 * documents, transmettre) — routes/candidatures.ts.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, getStoredUser, clearSession, go, EspaceShell, Alert } from './espace';

const PROFILES = [
  { type: 'SECURITY', key: 'securite', title: 'Sécurité & prévention', text: 'Agents de sécurité privée et professionnels de la prévention.' },
  { type: 'MEDICAL', key: 'sante', title: 'Santé & secours', text: 'Secouristes et professionnels de santé.' },
];

const SUBMITTED_MESSAGE = 'Votre candidature a bien été transmise à CIVIK. Vous pouvez suivre son instruction depuis votre espace.';

function fmtDate(d) { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return ''; } }
function fmtDateTime(d) { try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } }
function fmtSize(b) { return b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`; }

/* ── Page ─────────────────────────────────────────────────────────────── */
export function CandidaturePage() {
  const [app, setApp] = useState(undefined);      // undefined = chargement, null = aucune
  const [def, setDef] = useState(null);
  const [view, setView] = useState('dashboard');  // dashboard | questionnaire | formation | documents | transmettre
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [presetType, setPresetType] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/candidatures/me', { auth: true });
      setApp(r.application);
      if (r.application) {
        const d = await apiFetch(`/candidatures/definition?type=${r.application.type}`, { auth: true });
        setDef(d);
      }
    } catch (err) {
      if (err.status === 401) { clearSession(); toLogin(); return; }
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (!getStoredUser() || !localStorage.getItem('civik_access_token')) { toLogin(); return; }
    try {
      const p = new URLSearchParams(window.location.search).get('profil') || '';
      setPresetType(p === 'sante' ? 'MEDICAL' : p === 'securite' ? 'SECURITY' : '');
    } catch { /* rien */ }
    load();
  }, [load]);

  const start = async (type) => {
    setBusy(true); setError('');
    try {
      const r = await apiFetch('/candidatures', { method: 'POST', auth: true, body: { type } });
      setApp(r.application);
      setDef(await apiFetch(`/candidatures/definition?type=${type}`, { auth: true }));
      setView('questionnaire');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const update = (r, message) => { if (r && r.application) setApp(r.application); if (message) setInfo(message); };

  if (app === undefined) {
    return <EspaceShell title="Ma candidature CIVIK" wide><Alert kind="error">{error}</Alert><p className="espace-hint">Chargement…</p></EspaceShell>;
  }

  if (app === null) {
    return (
      <EspaceShell title="Ma candidature CIVIK" wide>
        <Alert kind="error">{error}</Alert>
        <div className="espace-card">
          <h2>Choisissez votre profil professionnel</h2>
          <p className="espace-hint">Votre candidature est rattachée à votre compte CIVIK. Une seule candidature par compte.</p>
          <div className="cand-profiles">
            {PROFILES.map((p) => (
              <div key={p.type} className={`cand-profile ${presetType === p.type ? 'cand-profile-preset' : ''}`}>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
                <button type="button" className="espace-btn" disabled={busy} onClick={() => start(p.type)}>COMMENCER MA CANDIDATURE</button>
              </div>
            ))}
          </div>
        </div>
        <p className="espace-hint" style={{ marginTop: 16 }}><a href="/mon-compte">Mon compte</a> · <a href="/#professionnels">Retour à la présentation du réseau</a></p>
      </EspaceShell>
    );
  }

  const pr = app.progress;
  const common = { app, def, setError, setInfo, update, back: () => { setView('dashboard'); setInfo(''); setError(''); window.scrollTo(0, 0); } };

  return (
    <EspaceShell title="Ma candidature CIVIK" wide>
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{info}</Alert>
      {view === 'dashboard' && <Dashboard {...common} pr={pr} open={(v) => { setView(v); setInfo(''); setError(''); window.scrollTo(0, 0); }} />}
      {view === 'questionnaire' && <Questionnaire {...common} />}
      {view === 'formation' && <Training {...common} />}
      {view === 'documents' && <Documents {...common} />}
      {view === 'transmettre' && <Submit {...common} pr={pr} />}
    </EspaceShell>
  );
}

function toLogin() {
  try { sessionStorage.setItem('civik_next', '/candidature'); } catch { /* rien */ }
  go('/connexion?next=/candidature');
}

/* ── Tableau de bord (§10) ─────────────────────────────────────────────── */
function Dashboard({ app, pr, open }) {
  const rows = [
    { key: 'questionnaire', label: 'Questionnaire', status: pr.steps.questionnaire, done: pr.steps.questionnaire === 'Complété' },
    { key: 'formation', label: 'Formation', status: pr.steps.training, done: pr.steps.training === 'Terminée' },
    { key: 'documents', label: 'Diplômes & justificatifs', status: pr.steps.documents, done: pr.steps.documents === 'Transmis' },
    { key: 'instruction', label: 'Instruction', status: pr.steps.instruction, done: app.status === 'VALIDATED' },
  ];
  const closed = app.status === 'VALIDATED' || app.status === 'REJECTED';
  const continueLabel = pr.nextStep === 'transmettre' ? 'TRANSMETTRE MA CANDIDATURE' : 'POURSUIVRE MA CANDIDATURE';
  return (
    <>
      <div className="espace-card cand-head">
        <div className="cand-head-row">
          <div>
            <p className="cand-kicker">{app.typeLabel}</p>
            <h2>{pr.stage}</h2>
            <p className="espace-hint">Candidature commencée le {fmtDate(app.createdAt)}{app.submittedAt ? ` · transmise le ${fmtDate(app.submittedAt)}` : ''}.</p>
          </div>
          <span className={`cand-badge cand-badge-${app.status.toLowerCase()}`}>{app.statusLabel}</span>
        </div>
        {pr.nextAction && app.editable ? <p className="cand-action" role="status">{pr.nextAction}</p> : null}
        {app.status === 'INFO_REQUESTED' && app.infoRequest ? <p className="espace-hint">Message de CIVIK : « {app.infoRequest} »</p> : null}
        {app.status === 'VALIDATED' ? <p className="cand-action cand-action-ok">Votre candidature est validée : vous pouvez accéder aux fonctionnalités prévues pour les professionnels partenaires CIVIK depuis <a href="/mon-compte">votre compte</a> et dans l'application.{app.decisionNote ? ` Message de CIVIK : « ${app.decisionNote} »` : ''}</p> : null}
        {app.status === 'REJECTED' ? <p className="cand-action cand-action-ko">Après étude, CIVIK n'a pas retenu votre candidature.{app.decisionNote ? ` Motif communiqué : « ${app.decisionNote} »` : ''}</p> : null}
        {app.status === 'RECEIVED' || app.status === 'UNDER_REVIEW' ? <p className="espace-hint">{SUBMITTED_MESSAGE}</p> : null}
        {app.editable && pr.nextStep ? <p><button type="button" className="espace-btn" onClick={() => open(pr.nextStep)}>{continueLabel}</button></p> : null}
      </div>

      <div className="espace-card">
        <h2>Avancement</h2>
        <ul className="cand-steps">
          {rows.map((r) => (
            <li key={r.key} className={r.done ? 'is-done' : ''}>
              <span className="cand-step-check" aria-hidden="true">{r.done ? '✓' : ''}</span>
              <span className="cand-step-label">{r.label}</span>
              <span className="cand-step-status">{r.status}</span>
              {r.key !== 'instruction' && !closed ? <button type="button" className="espace-btn-secondary cand-step-btn" onClick={() => open(r.key)}>{app.editable ? (r.done ? 'Revoir' : 'Ouvrir') : 'Consulter'}</button> : null}
            </li>
          ))}
        </ul>
      </div>

      {app.history && app.history.length ? (
        <div className="espace-card">
          <h2>Historique</h2>
          <ul className="cand-history">
            {app.history.slice().reverse().map((h, i) => (
              <li key={i}><span>{fmtDateTime(h.at)}</span><strong>{h.toLabel}</strong>{h.note ? <em>{h.note}</em> : null}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="espace-hint"><a href="/mon-compte">Mon compte</a> · <a href="/">Retour au site</a></p>
    </>
  );
}

/* ── Questionnaire (§8 étape 3) ────────────────────────────────────────── */
function Questionnaire({ app, def, update, setError, back }) {
  const fields = (def && def.questionnaire) || [];
  const initial = {};
  fields.forEach((f) => { const v = app.questionnaire ? app.questionnaire[f.name] : undefined; initial[f.name] = v !== undefined && v !== null ? v : (f.type === 'checkbox' ? false : f.type === 'multiselect' ? [] : ''); });
  const [answers, setAnswers] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (name, value) => setAnswers((a) => ({ ...a, [name]: value }));
  const readOnly = !app.editable;

  const submit = async (e) => {
    e.preventDefault(); if (readOnly) return;
    setBusy(true); setErrors({}); setError('');
    try {
      const payload = {};
      fields.forEach((f) => { const v = answers[f.name]; if (f.type === 'checkbox') payload[f.name] = !!v; else if (f.type === 'multiselect') payload[f.name] = v || []; else if (v !== '' && v !== undefined) payload[f.name] = v; else if (f.required) payload[f.name] = ''; });
      const r = await apiFetch('/candidatures/questionnaire', { method: 'PUT', auth: true, body: { answers: payload } });
      update(r, 'Questionnaire enregistré.');
      back();
    } catch (err) {
      if (err.details) { setErrors(err.details); setError('Certains champs sont à corriger.'); } else setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <form className="espace-card" onSubmit={submit} noValidate>
      <h2>Questionnaire professionnel — {app.typeLabel}</h2>
      {readOnly ? <p className="espace-hint">Votre dossier a été transmis : le questionnaire est en lecture seule.</p> : <p className="espace-hint">Les champs marqués * sont obligatoires.</p>}
      {fields.map((f) => <QField key={f.name} f={f} value={answers[f.name]} onChange={(v) => set(f.name, v)} error={errors[f.name]} disabled={readOnly} />)}
      <div className="cand-actions">
        <button type="button" className="espace-btn-secondary" onClick={back}>Retour</button>
        {!readOnly ? <button type="submit" className="espace-btn" disabled={busy}>VALIDER ET CONTINUER</button> : null}
      </div>
    </form>
  );
}

function QField({ f, value, onChange, error, disabled }) {
  const id = `q-${f.name}`;
  const err = error ? <span className="cand-error" id={`${id}-err`}>{error}</span> : null;
  const aria = { 'aria-invalid': error ? 'true' : undefined, 'aria-describedby': error ? `${id}-err` : undefined, disabled };
  if (f.type === 'checkbox') {
    return (
      <div className={`cand-field cand-field-check ${error ? 'has-error' : ''}`}>
        <label htmlFor={id}><input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} {...aria} /> <span>{f.label}{f.required ? ' *' : ''}</span></label>
        {err}
      </div>
    );
  }
  if (f.type === 'multiselect') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <fieldset className={`cand-field ${error ? 'has-error' : ''}`}>
        <legend>{f.label}{f.required ? ' *' : ''}</legend>
        {f.options.map((o) => (
          <label key={o} className="cand-check-inline"><input type="checkbox" checked={arr.includes(o)} disabled={disabled} onChange={(e) => onChange(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))} /> <span>{o}</span></label>
        ))}
        {err}
      </fieldset>
    );
  }
  return (
    <div className={`cand-field ${error ? 'has-error' : ''}`}>
      <label htmlFor={id}>{f.label}{f.required ? ' *' : ''}</label>
      {f.type === 'select' ? (
        <select id={id} value={value || ''} onChange={(e) => onChange(e.target.value)} {...aria}>
          <option value="">— Choisir —</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : f.type === 'textarea' ? (
        <textarea id={id} rows="4" value={value || ''} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} {...aria} />
      ) : (
        <input id={id} type={f.type === 'number' ? 'number' : 'text'} value={value || ''} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} {...aria} />
      )}
      {f.hint ? <span className="espace-hint">{f.hint}</span> : null}
      {err}
    </div>
  );
}

/* ── Formation en ligne (§8 étape 4) ───────────────────────────────────── */
function Training({ app, def, update, setError, back }) {
  const modules = (def && def.training) || [];
  const done = app.trainingModules || {};
  const firstOpen = modules.findIndex((m) => !done[m.id] && m.required !== false);
  const [current, setCurrent] = useState(null);   // id du module ouvert
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const readOnly = !app.editable;
  const started = Object.keys(done).length > 0;

  const openModule = (id) => { setCurrent(id); setAnswers({}); setFeedback(''); setError(''); window.scrollTo(0, 0); };
  const mod = modules.find((m) => m.id === current);

  const validate = async (e) => {
    e.preventDefault(); if (!mod) return;
    if (mod.questions.some((q) => answers[q.id] === undefined)) { setFeedback('Répondez à toutes les questions avant de valider.'); return; }
    setBusy(true); setFeedback('');
    try {
      const r = await apiFetch(`/candidatures/formation/${mod.id}`, { method: 'POST', auth: true, body: { answers } });
      if (!r.passed) { setFeedback(r.message || 'Certaines réponses sont incorrectes.'); return; }
      update(r, r.allDone ? 'Formation terminée.' : `Module « ${mod.title} » validé.`);
      const next = modules.find((m) => !(r.application.trainingModules || {})[m.id]);
      if (next) openModule(next.id); else { setCurrent(null); back(); }
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (mod) {
    return (
      <form className="espace-card" onSubmit={validate}>
        <p className="cand-kicker">Formation CIVIK · module {modules.indexOf(mod) + 1} / {modules.length} · {mod.duration}</p>
        <h2>{mod.title}</h2>
        {mod.paragraphs.map((p, i) => <p key={i} className="cand-module-p">{p}</p>)}
        <h3 className="cand-h3">Vérifiez votre compréhension</h3>
        {mod.questions.map((q) => (
          <fieldset key={q.id} className="cand-field">
            <legend>{q.q}</legend>
            {q.options.map((o, idx) => (
              <label key={idx} className="cand-check-inline"><input type="radio" name={`${mod.id}-${q.id}`} checked={answers[q.id] === idx} disabled={readOnly || !!done[mod.id]} onChange={() => setAnswers((a) => ({ ...a, [q.id]: idx }))} /> <span>{o}</span></label>
            ))}
          </fieldset>
        ))}
        {feedback ? <p className="cand-error" role="alert">{feedback}</p> : null}
        <div className="cand-actions">
          <button type="button" className="espace-btn-secondary" onClick={() => setCurrent(null)}>Retour aux modules</button>
          {!readOnly && !done[mod.id] ? <button type="submit" className="espace-btn" disabled={busy}>VALIDER ET CONTINUER</button> : null}
        </div>
      </form>
    );
  }

  return (
    <div className="espace-card">
      <h2>Formation CIVIK en ligne</h2>
      <p className="espace-hint">{modules.length} modules courts, à suivre dans l'ordre. Chaque module se termine par quelques questions.</p>
      <ol className="cand-modules">
        {modules.map((m, i) => (
          <li key={m.id} className={done[m.id] ? 'is-done' : ''}>
            <span className="cand-step-check" aria-hidden="true">{done[m.id] ? '✓' : i + 1}</span>
            <span className="cand-step-label">{m.title}{m.required === false ? ' (facultatif)' : ''}<small>{m.duration}{done[m.id] ? ` · validé le ${fmtDate(done[m.id].completedAt)}` : ''}</small></span>
            <button type="button" className="espace-btn-secondary cand-step-btn" onClick={() => openModule(m.id)}>{done[m.id] ? 'Relire' : 'Ouvrir'}</button>
          </li>
        ))}
      </ol>
      <div className="cand-actions">
        <button type="button" className="espace-btn-secondary" onClick={back}>Retour</button>
        {!readOnly && firstOpen >= 0 ? <button type="button" className="espace-btn" onClick={() => openModule(modules[firstOpen].id)}>{started ? 'POURSUIVRE LA FORMATION' : 'COMMENCER LA FORMATION'}</button> : null}
      </div>
    </div>
  );
}

/* ── Diplômes et justificatifs (§8 étape 5) ────────────────────────────── */
function Documents({ app, def, update, setError, back }) {
  const expected = (def && def.documents) || [];
  const [type, setType] = useState(expected[0] ? expected[0].type : '');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const readOnly = !app.editable;

  const add = async (e) => {
    e.preventDefault();
    const file = fileRef.current && fileRef.current.files && fileRef.current.files[0];
    if (!file) { setError('Choisissez un fichier (PDF, JPG, PNG ou WEBP, 10 Mo maximum).'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Fichier trop volumineux (10 Mo maximum).'); return; }
    setBusy(true); setError('');
    try {
      const fd = new FormData(); fd.append('documentType', type); fd.append('file', file);
      const r = await apiFetch('/candidatures/documents', { method: 'POST', auth: true, body: fd });
      update(r, 'Justificatif ajouté.');
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true); setError('');
    try { update(await apiFetch(`/candidatures/documents/${id}`, { method: 'DELETE', auth: true }), 'Justificatif retiré.'); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const has = (t) => app.documents.some((d) => d.documentType === t && d.status !== 'rejected');

  return (
    <div className="espace-card">
      <h2>Diplômes & justificatifs</h2>
      <p className="espace-hint">Documents attendus pour votre profil ({app.typeLabel}) :</p>
      <ul className="cand-expected">
        {expected.map((d) => <li key={d.type} className={has(d.type) ? 'is-done' : ''}><span className="cand-step-check" aria-hidden="true">{has(d.type) ? '✓' : ''}</span>{d.label}{d.required ? <strong> (requis)</strong> : null}</li>)}
      </ul>
      {app.documents.length ? (
        <ul className="cand-docs">
          {app.documents.map((d) => (
            <li key={d.id}>
              <span className="cand-doc-name">{(expected.find((x) => x.type === d.documentType) || {}).label || d.documentType} — {d.fileName} <small>({fmtSize(d.fileSizeBytes)})</small></span>
              <span className={`cand-doc-status cand-doc-${d.status}`}>{d.status === 'approved' ? 'Accepté' : d.status === 'rejected' ? 'Refusé' : 'Reçu'}{d.reviewNote ? ` — ${d.reviewNote}` : ''}</span>
              {!readOnly ? <button type="button" className="espace-btn-secondary cand-step-btn" disabled={busy} onClick={() => remove(d.id)}>Retirer</button> : null}
            </li>
          ))}
        </ul>
      ) : <p className="espace-hint">Aucun justificatif transmis pour le moment.</p>}
      {!readOnly ? (
        <form onSubmit={add} className="cand-upload">
          <div className="cand-field">
            <label htmlFor="doc-type">Type de document</label>
            <select id="doc-type" value={type} onChange={(e) => setType(e.target.value)}>{expected.map((d) => <option key={d.type} value={d.type}>{d.label}</option>)}</select>
          </div>
          <div className="cand-field">
            <label htmlFor="doc-file">Fichier (PDF, JPG, PNG ou WEBP, 10 Mo maximum)</label>
            <input id="doc-file" type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" />
          </div>
          <div className="cand-actions">
            <button type="button" className="espace-btn-secondary" onClick={back}>Retour</button>
            <button type="submit" className="espace-btn" disabled={busy}>AJOUTER UN JUSTIFICATIF</button>
          </div>
        </form>
      ) : <div className="cand-actions"><button type="button" className="espace-btn-secondary" onClick={back}>Retour</button></div>}
    </div>
  );
}

/* ── Transmission (§8 étape 6) ─────────────────────────────────────────── */
function Submit({ app, pr, update, setError, back }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const send = async () => {
    setBusy(true); setError('');
    try { const r = await apiFetch('/candidatures/transmettre', { method: 'POST', auth: true }); update(r, ''); setDone(true); window.scrollTo(0, 0); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  if (done) {
    return (
      <div className="espace-card">
        <h2>Candidature transmise</h2>
        <p className="cand-action cand-action-ok" role="status">{SUBMITTED_MESSAGE}</p>
        <div className="cand-actions"><button type="button" className="espace-btn" onClick={back}>Voir mon avancement</button></div>
      </div>
    );
  }
  return (
    <div className="espace-card">
      <h2>Transmettre ma candidature</h2>
      <ul className="cand-steps">
        <li className={pr.steps.questionnaire === 'Complété' ? 'is-done' : ''}><span className="cand-step-check" aria-hidden="true">{pr.steps.questionnaire === 'Complété' ? '✓' : ''}</span><span className="cand-step-label">Questionnaire</span><span className="cand-step-status">{pr.steps.questionnaire}</span></li>
        <li className={pr.steps.training === 'Terminée' ? 'is-done' : ''}><span className="cand-step-check" aria-hidden="true">{pr.steps.training === 'Terminée' ? '✓' : ''}</span><span className="cand-step-label">Formation</span><span className="cand-step-status">{pr.steps.training}</span></li>
        <li className={pr.steps.documents === 'Transmis' ? 'is-done' : ''}><span className="cand-step-check" aria-hidden="true">{pr.steps.documents === 'Transmis' ? '✓' : ''}</span><span className="cand-step-label">Diplômes & justificatifs</span><span className="cand-step-status">{pr.steps.documents} ({app.documents.length})</span></li>
      </ul>
      <p className="espace-hint">En transmettant votre dossier, vous confirmez l'exactitude des informations fournies. Votre dossier ne sera plus modifiable pendant son instruction, sauf si CIVIK vous demande des informations complémentaires.</p>
      <div className="cand-actions">
        <button type="button" className="espace-btn-secondary" onClick={back}>Retour</button>
        <button type="button" className="espace-btn" disabled={busy || !pr.canSubmit} onClick={send}>TRANSMETTRE MA CANDIDATURE</button>
      </div>
      {!pr.canSubmit && pr.nextAction ? <p className="cand-error">{pr.nextAction}</p> : null}
    </div>
  );
}
